import { StorageManager } from 'src/app/common/storage-manager.class';
import { FormRequestTypes } from 'src/app/common/classes/form-request';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import {
  Form,
  FormField,
  FormSection,
  FormType,
} from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';
import {
  InputType,
} from 'src/app/common/components/reactive-form-builder/interfaces/input.interface';
import { FertirrigacionListsDefinition } from './list-variables';
import { FertirrigacionEnvironmentDefinition } from '../../environment-variables';
import { FertirrigacionModel } from 'src/app/models/tareas/fertirrigacion.model';
import { FilteredValuesInterface } from 'src/app/common/components/form-builder/form-builder.component';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { SectorModel } from 'src/app/models/form-common/sector.model';

export class FertirrigacionFormDefinition {
  public formType!: FormType;

  public env: FertirrigacionEnvironmentDefinition =
    new FertirrigacionEnvironmentDefinition();

  public lists: FertirrigacionListsDefinition =
    new FertirrigacionListsDefinition();

  public formFields!: Form<FertirrigacionModel>;

  public fieldNames: FormField<any>[] = [];

  public fieldLabels: string[] = [];

  public lastFieldEdited = '';

  public tag = '';

  public requestType: FormRequestTypes = FormRequestTypes.CREATE;

  public cliente = StorageManager.getClient();

  public usuario = StorageManager.getUser();

  public model: FertirrigacionModel = {
    id_cliente: +(this.cliente?.id ?? 0),
    id_usuario: +StorageManager.getUser().id,
  };

  public openDialog = false;

  public getFormFields(): FormField<any>[] {
    const formFields: FormField<any>[] = [
      {
        name: this.formType < 3 ? 'id_finca' : 'finca',
        vinculatedFields: undefined,
        label: this.env.fincaName,
        input: {
          type: InputType.DROPDOWN_SEARCH,
          isEditable: true,
          data: this.lists.fincas,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: (it: FilteredValuesInterface<FincasModel>): boolean => {
              if (
                !this.env.hasClients ||
                this.formType === 2 ||
                !this.env.userCanSeeClientes
              ) {
                return true;
              } else if (Boolean(StorageManager.getClient())) {
                return it.value?.id_cliente === StorageManager.getClient()?.id;
              }
              return true;
            },
          },
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: true,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.hasFincas,
        },
        order: 1,
      },
      {
        name: this.formType < 3 ? 'ids_sectores' : 'sectores_nombres',
        vinculatedFields: undefined,
        label: this.env.sectoresName,
        input: {
          type: InputType.MULTISELECT_SEARCH,
          isEditable: true,
          data: this.lists.sectores,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: (it: FilteredValuesInterface<SectorModel>) =>
              it.value?.id_finca === this.lists.fincas.selected?.id,
          },
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showSectores,
        },
        order: 2,
      },
      {
        name: this.formType < 3 ? 'ids_parcelas' : 'parcelas_nombres',
        vinculatedFields: undefined,
        label: this.env.parcelasName,
        input: {
          type: InputType.MULTISELECT_SEARCH,
          isEditable: true,
          data: this.lists.parcelas,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: (it: FilteredValuesInterface<ParcelasModel>): boolean => {
              if (it.value?.activo?.toString() !== '1') {
                return false;
              }

              if (
                this.lists.sectores.selected !== undefined &&
                this.lists.sectores.selected.length > 0
              ) {
                return this.lists.sectores.selected
                  .map((sector: SectorModel) => sector.id)
                  .includes(it.value.id_sector);
              }

              if (Boolean(this.lists.fincas.selected)) {
                return it.value.id_finca === this.lists.fincas.selected?.id;
              }
              return false;
            },
          },
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired:
            this.env.appName !== 'pyf' && this.env.appName !== 'donjardin',
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 3,
      },
      {
        name: 'fecha',
        vinculatedFields: undefined,
        label: 'Fecha',
        input: { type: InputType.CALENDAR, isEditable: true },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 4,
      },
      {
        name: 'sistema_riego',
        vinculatedFields: undefined,
        label: 'Sistema de Riego',
        input: {
          type: InputType.DROPDOWN,
          isEditable: true,
          data: this.lists.sistemasRiego,
          dropdownConfiguration: {
            valuePrimaryKey: 'codigo_SIEX',
          },
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 5,
      },
      {
        name: 'volumen_riego',
        vinculatedFields: undefined,
        label: 'Volumen (m³/ha/año)',
        input: { type: InputType.NUMERIC, isEditable: true },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 6,
      },
      {
        name: 'n_agua',
        vinculatedFields: undefined,
        label: 'N nítrico del agua (mg/l)',
        input: { type: InputType.NUMERIC, isEditable: true },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 7,
      },
      {
        name: 'p205',
        vinculatedFields: undefined,
        label: 'P205 soluble en agua (mg/l)',
        input: { type: InputType.NUMERIC, isEditable: true },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 8,
      },
      {
        name: 'aplicacion_residuos_r10',
        vinculatedFields: undefined,
        label: 'Aplicación de residuos R10',
        input: { type: InputType.NUMERIC, isEditable: true },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 10,
      },
      {
        name: 'documento_aplicacion',
        vinculatedFields: undefined,
        label: 'Documento de Aplicación',
        input: {
          type: InputType.SWITCH,
          isEditable: true,
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 11,
      },
      {
        name: 'id_comunidad_regantes',
        vinculatedFields: undefined,
        label: 'Comunidad de Regantes',
        input: {
          type: InputType.DROPDOWN_SEARCH,
          isEditable: true,
          data: this.lists.comunidadesRegantes,
          dropdownConfiguration: {
            valuePrimaryKey: 'id'
          }
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 12,
        buttons: [
          {
            class: 'btn btn-success border rounded-circle shadow-sm p-0 d-flex align-items-center justify-content-center',
            style: `
              width: 26px;
              height: 26px;
              margin-left: 6px;
              font-size: .95rem;
              transform: translateY(-0.5rem);
            `,
            icon: 'fa fa-plus',
            label: '',
            tooltip: 'Añadir nuevo valor',
            tooltipPosition: 'top',
            function: () => this.openDialog = true
          }
        ],
      },
      {
        name: 'observaciones',
        vinculatedFields: undefined,
        label: 'Observaciones',
        input: {
          type: InputType.TEXTAREA,
          isEditable: true,
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 13,
      },
    ];

    this.fieldNames = this.fieldNames.concat(
      formFields.filter((it) => it.conditions.isVisible)
    );
    this.fieldLabels = this.fieldLabels.concat(
      formFields.filter((it) => it.conditions.isVisible).map((it) => it.label)
    );
    return formFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  private getFormSections(): FormSection<FertirrigacionModel>[] {
    const sections: FormSection<FertirrigacionModel>[] = [
      {
        name: '',
        width: 12,
        isVisible: true,
        fields: this.getFormFields(),
        order: 1,
      },
      {
        name: '',
        width: 6,
        isVisible: true,
        fields: this.getHiddenFields(),
        order: 2,
      },
    ];

    return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  private getHiddenFields(): FormField<any>[] {
    const hiddenFields: FormField<any>[] = [
      {
        name: 'id',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          defaultValue: this.model.id,
          isEditable: false,
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 1,
      },
      {
        name: 'id_cliente',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          defaultValue: Boolean(this.cliente?.id)
            ? this.cliente?.id
            : this.lists.fincas.selected?.id_cliente,
          isEditable: false,
        },
        updateValue: () =>
          Boolean(this.cliente?.id)
            ? this.cliente?.id
            : this.lists.fincas.selected?.id_cliente,
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.hasClients && this.env.userCanSeeClientes,
        },
        order: 2,
      },
      {
        name: 'id_usuario',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          defaultValue: this.getCurrentUser(),
          isEditable: false,
        },
        updateValue: () => this.getCurrentUser(),
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 3,
      },
      {
        name: 'cultivo',
        vinculatedFields: undefined,
        label: '',
        input: { type: InputType.HIDDEN, isEditable: false },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        updateValue: () =>
          (this.model.ids_parcelas as unknown as ParcelasModel[])?.map((parcela: ParcelasModel) => parcela?.cultivo).join(';'),
        order: 4,
      },
      {
        name: 'variedad',
        vinculatedFields: undefined,
        label: '',
        input: { type: InputType.HIDDEN, isEditable: false },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        updateValue: () =>
          (this.model.ids_parcelas as unknown as ParcelasModel[])
            ?.map((parcela: ParcelasModel) => parcela?.variedad)
            .join(';'),
        order: 5,
      },
      // aportacion_teorica_nitrogeno: calculado en PHP (GET de fertirrigacion),
      // ya no se envía desde Angular al guardar.
    ];

    return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  private getCurrentUser(): string {
    const currentUserId =
      this.formType !== FormType.EDITION ? this.usuario?.id ?? '' : '';
    const fincaAssignedUserId = Boolean(this.lists.fincas.selected)
      ? this.lists.fincas.selected.id_usuario
      : '';
    const userId = this.env.showTareasMto ? fincaAssignedUserId : currentUserId;

    return userId;
  }

  constructor(
    type: FormRequestTypes,
    _model: Record<string, string>,
    table: (string[] | string[][])[] | null
  ) {
    this.formType = +type;

    this.formFields = {
      name: 'fertirrigacion',
      header: 'Riego',
      type: this.formType,
      sections: this.getFormSections(),
      table: { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
      uploadButton: {
        isEnabled: false,
        label: ''
      },
      signed: false,
    };
  }
}